import type { BalanceTransactionType, RecordingPeriod } from '@autodialer/shared';

import { api } from './client';

/**
 * Hauptadmin-Schnittstelle (10_main_admin.md).
 *
 * Bewusst klein: Kundenliste, Kundendetail, Gesamtexport, Live-Gespräche,
 * Mithören und der kampagnenweise Aufnahme-Download. Es gibt hier
 * ausdrücklich **keine** Datensatz- oder Leadabfrage - der Systemadmin
 * bekommt die Daten als Export, nicht als Oberfläche zum Durchsuchen.
 */

export interface PlatformTenantRow {
  id: string;
  customerNumber: string;
  accountName: string | null;
  ownerEmail: string;
  status: string;
  registeredAt: string;
  balance: string;
  pricePerMinute: string;
  callerCount: number;
  campaignCount: number;
  totalMinutes: number;
}

export interface PlatformTenantDetail {
  account: {
    id: string;
    customerNumber: string;
    accountName: string | null;
    ownerEmail: string;
    status: string;
    locale: string;
    balance: string;
    pricePerMinute: string;
    registeredAt: string;
    suspendedAt: string | null;
    deletedAt: string | null;
  };
  usage: {
    totalMinutes: number;
    connectedCalls: number;
    attempts: number;
    minutesToday: number;
    lastCallAt: string | null;
  };
  campaigns: {
    id: string;
    name: string;
    isActive: boolean;
    campaignMode: string;
    contacts: number;
  }[];
  settings: {
    campaignCount: number;
    callerCount: number;
    defaultCountryCodes: string[];
    inboundCampaigns: number;
    queueCampaigns: number;
    recordingCampaigns: number;
  };
}

export interface PlatformLiveCall {
  callId: string;
  campaignId: string;
  campaignName: string;
  userId: string | null;
  callerName: string | null;
  contactId: string | null;
  contactName: string | null;
  phoneNumber: string;
  connectedAt: string;
}

export interface PlatformRecordingSummary {
  campaignId: string;
  campaignName: string;
  count: number;
  oldestAt: string | null;
  newestAt: string | null;
  totalBytes: number;
}

/**
 * Eine Buchung, wie der Systemadmin sie sieht.
 *
 * Er sieht zusaetzlich die interne Notiz und die Vorgangsnummer des
 * Zahlungsanbieters - der Kunde nicht (09_billing_payments.md).
 */
export interface PlatformBalanceTransaction {
  id: string;
  type: BalanceTransactionType;
  amount: string;
  balanceBefore: string;
  balanceAfter: string;
  source: string;
  externalId: string | null;
  internalNote: string | null;
  visibleNote: string | null;
  createdAt: string;
}

const tenantPath = (tenantId: string): string => '/platform/tenants/' + tenantId;

export const platformApi = {
  listTenants: () => api.get<{ tenants: PlatformTenantRow[] }>('/platform/tenants'),
  tenant: (tenantId: string) => api.get<PlatformTenantDetail>(tenantPath(tenantId)),

  suspend: (tenantId: string) => api.post<{ account: unknown }>(tenantPath(tenantId) + '/suspend'),
  unsuspend: (tenantId: string) => api.post<{ account: unknown }>(tenantPath(tenantId) + '/unsuspend'),

  /**
   * `Alle Kundendaten herunterladen`.
   *
   * Der Download läuft als normale Anfrage mit Sitzungscookie - die Adresse
   * allein genügt nicht, und sie ist nicht erratbar nutzbar.
   */
  exportUrl: (tenantId: string) => '/api' + tenantPath(tenantId) + '/export',

  liveCalls: (tenantId: string) =>
    api.get<{ calls: PlatformLiveCall[] }>(tenantPath(tenantId) + '/live-calls'),

  listen: (callId: string) =>
    api.post<{ listening: boolean }>('/platform/calls/' + callId + '/listen'),
  stopListening: (callId: string) =>
    api.delete<{ stopped: boolean }>('/platform/calls/' + callId + '/listen'),
  listeningSession: () =>
    api.get<{ listening: boolean; callId: string | null; startedAt: string | null }>(
      '/platform/listening',
    ),

  // --- Guthaben ---
  balance: (tenantId: string) =>
    api.get<{ balance: string; transactions: PlatformBalanceTransaction[] }>(
      tenantPath(tenantId) + '/balance',
    ),
  /** Manuelle Gutschrift. Der Mindestaufladebetrag gilt hier nicht. */
  creditBalance: (tenantId: string, amount: string, visibleNote: string | null) =>
    api.post<{ balance: string; transaction: PlatformBalanceTransaction }>(
      tenantPath(tenantId) + '/balance/credit',
      { amount, visibleNote },
    ),
  /** Manuelle Abbuchung. Reicht das Guthaben nicht, wird nichts gebucht. */
  debitBalance: (tenantId: string, amount: string, visibleNote: string | null) =>
    api.post<{ balance: string; transaction: PlatformBalanceTransaction }>(
      tenantPath(tenantId) + '/balance/debit',
      { amount, visibleNote },
    ),

  recordings: (tenantId: string) =>
    api.get<{ campaigns: PlatformRecordingSummary[] }>(tenantPath(tenantId) + '/recordings'),
  recordingExportUrl: (tenantId: string, campaignId: string, period: RecordingPeriod) =>
    '/api' + tenantPath(tenantId) + '/campaigns/' + campaignId + '/recordings/export?period=' + period,
};
